import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function MissionPreview() {
  const { t } = useTranslation();
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    if (leftRef.current) {
      leftRef.current.classList.add("reveal-left");
      observer.observe(leftRef.current);
    }
    if (rightRef.current) {
      rightRef.current.classList.add("reveal-right");
      observer.observe(rightRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      icon: "ri-book-open-line",
      titleKey: "mission.pillar.education",
      descKey: "mission.pillar.education.desc",
      color: "bg-amber-50 text-amber-600",
    },
    {
      icon: "ri-heart-pulse-line",
      titleKey: "mission.pillar.healthcare",
      descKey: "mission.pillar.healthcare.desc",
      color: "bg-rose-50 text-rose-600",
    },
    {
      icon: "ri-community-line",
      titleKey: "mission.pillar.community",
      descKey: "mission.pillar.community.desc",
      color: "bg-teal-50 text-[#4ECDC4]",
    },
    {
      icon: "ri-seedling-line",
      titleKey: "mission.pillar.sustainability",
      descKey: "mission.pillar.sustainability.desc",
      color: "bg-green-50 text-green-600",
    },
  ];

  return (
    <section className="py-20 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left: Text */}
          <div ref={leftRef}>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
              {t("mission.tag")}
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1B2838] leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("mission.title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-5">{t("mission.body1")}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">{t("mission.body2")}</p>
            <Link
              to="/mission"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B2838] text-white rounded-full font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors"
            >
              {t("mission.cta")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* Right: Pillars */}
          <div ref={rightRef} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pillars.map((pillar) => (
              <div
                key={pillar.titleKey}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#4ECDC4]/30 transition-all duration-300"
              >
                <div
                  className={`w-11 h-11 flex items-center justify-center rounded-lg ${pillar.color} mb-4`}
                >
                  <i className={`${pillar.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1B2838] text-base mb-2">{t(pillar.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(pillar.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
